import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  Search, Upload, BookOpen, FileText, FileSpreadsheet, Presentation,
  ArrowLeft, Filter, SortAsc, SortDesc, Tag, Clock, ChevronDown,
  Trash2, Download, Eye, Settings,
} from 'lucide-react'
import { api, KbDocSummary, KbCategory } from '../lib/api'
import { KbUploadModal } from '../components/solutions/KbUploadModal'
import { KbDocumentViewer } from '../components/solutions/KbDocumentViewer'
import { KbCategoryManager } from '../components/solutions/KbCategoryManager'
import { useDoctrineMode } from '../hooks/useDoctrineMode'
import { LiteModeNotice } from '../components/LiteModeNotice'

function docTypeIcon(mimeType: string | null, sourceType: string) {
  if (sourceType === 'paste' || sourceType === 'solution_output') {
    return <FileText className="w-4 h-4 text-indigo-500" />
  }
  switch (mimeType) {
    case 'application/pdf':
      return <FileText className="w-4 h-4 text-red-500" />
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return <FileText className="w-4 h-4 text-blue-500" />
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.ms-excel':
      return <FileSpreadsheet className="w-4 h-4 text-green-500" />
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return <Presentation className="w-4 h-4 text-orange-500" />
    case 'text/csv':
    case 'text/tab-separated-values':
      return <FileSpreadsheet className="w-4 h-4 text-teal-500" />
    default:
      return <FileText className="w-4 h-4 text-gray-500" />
  }
}

function sourceLabel(type: string) {
  switch (type) {
    case 'upload': return 'Uploaded'
    case 'paste': return 'Pasted'
    case 'solution_output': return 'From Solution'
    default: return type
  }
}

function formatBytes(bytes: number | null) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

type SortBy = 'created_at' | 'updated_at' | 'title'
type SortOrder = 'asc' | 'desc'

export function SolutionKnowledgeBasePage() {
  const { isLite, isFeatureAvailable } = useDoctrineMode()
  if (isLite && !isFeatureAvailable('solutions-kb')) {
    return <LiteModeNotice feature="Solutions Knowledge Base" />
  }
  return <SolutionKnowledgeBasePageInner />
}

function SolutionKnowledgeBasePageInner() {
  const [documents, setDocuments] = useState<KbDocSummary[]>([])
  const [categories, setCategories] = useState<KbCategory[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  // Filters
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [sourceFilter, setSourceFilter] = useState<string>('')
  const [sortBy, setSortBy] = useState<SortBy>('updated_at')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [page, setPage] = useState(0)
  const pageSize = 25

  // Modals
  const [showUpload, setShowUpload] = useState(false)
  const [viewingDocId, setViewingDocId] = useState<string | null>(null)
  const [showCategoryManager, setShowCategoryManager] = useState(false)

  const searchTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const loadDocuments = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string> = {
        sortBy,
        sortOrder,
        limit: String(pageSize),
        offset: String(page * pageSize),
      }
      if (search) params.search = search
      if (categoryFilter) params.categoryId = categoryFilter
      if (sourceFilter) params.sourceType = sourceFilter

      const result = await api.solutions.kb.list(params)
      setDocuments(result.documents)
      setTotal(result.total)
    } catch (err) {
      console.error('Failed to load KB documents:', err)
    } finally {
      setLoading(false)
    }
  }, [search, categoryFilter, sourceFilter, sortBy, sortOrder, page])

  const loadCategories = useCallback(async () => {
    try {
      const cats = await api.solutions.kb.categories.list()
      setCategories(cats)
    } catch (err) {
      console.error('Failed to load categories:', err)
    }
  }, [])

  useEffect(() => { loadCategories() }, [loadCategories])
  useEffect(() => { loadDocuments() }, [loadDocuments])

  function handleSearchChange(value: string) {
    setSearch(value)
    setPage(0)
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {}, 300)
  }

  async function handleArchive(id: string) {
    if (!confirm('Archive this document?')) return
    await api.solutions.kb.delete(id)
    loadDocuments()
  }

  async function handleDownload(doc: KbDocSummary) {
    try {
      const blob = await api.solutions.kb.download(doc.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.filename || `${doc.title}.md`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download failed:', err)
    }
  }

  function toggleSort(col: SortBy) {
    if (sortBy === col) {
      setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortBy(col)
      setSortOrder('desc')
    }
    setPage(0)
  }

  const totalPages = Math.ceil(total / pageSize)

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/solutions"
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-indigo-600" />
                Knowledge Base
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {total} document{total !== 1 ? 's' : ''} — instruction sets, architectures, and reference materials
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCategoryManager(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              title="Manage Categories"
            >
              <Settings className="w-4 h-4" />
              Categories
            </button>
            <button
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-gray-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search documents..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
            />
          </div>

          {/* Category filter */}
          <div className="relative">
            <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setPage(0) }}
              className="pl-8 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none appearance-none"
            >
              <option value="">All Categories</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>

          {/* Source type filter */}
          <div className="relative">
            <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <select
              value={sourceFilter}
              onChange={(e) => { setSourceFilter(e.target.value); setPage(0) }}
              className="pl-8 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none appearance-none"
            >
              <option value="">All Sources</option>
              <option value="upload">Uploaded</option>
              <option value="paste">Pasted</option>
              <option value="solution_output">From Solution</option>
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Document table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loading && documents.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <div className="animate-pulse w-8 h-8 rounded-full bg-indigo-100 mx-auto mb-2" />
              <p className="text-sm">Loading documents...</p>
            </div>
          </div>
        ) : documents.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <BookOpen className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm font-medium">No documents yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Upload files, paste content, or save outputs from the Solutions Builder
              </p>
              <button
                onClick={() => setShowUpload(true)}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload First Document
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">
                    <button
                      onClick={() => toggleSort('title')}
                      className="inline-flex items-center gap-1 hover:text-gray-900"
                    >
                      Title
                      {sortBy === 'title' && (sortOrder === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />)}
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Source</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Size</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Tags</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">
                    <button
                      onClick={() => toggleSort('updated_at')}
                      className="inline-flex items-center gap-1 hover:text-gray-900"
                    >
                      <Clock className="w-3 h-3" />
                      Updated
                      {sortBy === 'updated_at' && (sortOrder === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />)}
                    </button>
                  </th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {documents.map(doc => (
                  <tr
                    key={doc.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setViewingDocId(doc.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {docTypeIcon(doc.mime_type, doc.source_type)}
                        <span className="font-medium text-gray-900 truncate max-w-xs">
                          {doc.title}
                        </span>
                      </div>
                      {doc.description && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs ml-6">
                          {doc.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {doc.category_name ? (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: doc.category_color || '#6366f1' }}
                        >
                          {doc.category_name}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">{sourceLabel(doc.source_type)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">{formatBytes(doc.file_size_bytes)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {doc.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="inline-flex px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">
                            {tag}
                          </span>
                        ))}
                        {doc.tags.length > 3 && (
                          <span className="text-[10px] text-gray-400">+{doc.tags.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">{formatDate(doc.updated_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => setViewingDocId(doc.id)}
                          className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                          title="View"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDownload(doc)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Download"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleArchive(doc.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Archive"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-xs text-gray-500">
                  Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="px-2 text-xs text-gray-500">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showUpload && (
        <KbUploadModal
          categories={categories}
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            setShowUpload(false)
            loadDocuments()
          }}
        />
      )}

      {viewingDocId && (
        <KbDocumentViewer
          documentId={viewingDocId}
          categories={categories}
          onClose={() => setViewingDocId(null)}
          onUpdated={loadDocuments}
        />
      )}

      {showCategoryManager && (
        <KbCategoryManager
          categories={categories}
          onClose={() => setShowCategoryManager(false)}
          onChanged={loadCategories}
        />
      )}
    </div>
  )
}
